import { VdsBasicSliderContinuous } from '@volt/volt-extended-components-react';
import { VdsBasicSliderContinuousCustomEvent } from '@volt/volt-basic-components';
import { ReactElement, RefObject, useMemo, useRef } from 'react';
import { debounce } from '@cas/utils/debounce';

interface SliderProps {
  elementRef?: RefObject<HTMLVdsBasicSliderContinuousElement>;
  minValue?: number;
  maxValue?: number;
  value?: number;
  onChangeCallback?: (value: number) => void;
  debounceDelay?: number; // ms
  step?: number;
  isDisabled?: boolean;
}

export const Slider = ({
  elementRef,
  minValue,
  maxValue,
  value: parentValue,
  onChangeCallback,
  debounceDelay = 0, // Default to no debounce.
  step = 1, // Default to a step of 1 for guaranteed accuracy of start/end values when dealing with integers within variable ranges.
  isDisabled = false,
}: SliderProps): ReactElement => {
  const valueRef = useRef<number | undefined>(parentValue);

  // Update the valueRef whenever the parent value has been updated, and the internal value is out of sync.
  if (typeof parentValue === 'number' && valueRef.current !== parentValue) {
    valueRef.current = parentValue;
  }

  // Create a debounced handler function for the onChangeInput event if a callback has been provided.
  const onChangeInput = useMemo<
    ((event: VdsBasicSliderContinuousCustomEvent<number>) => void) | undefined
  >(() => {
    if (onChangeCallback) {
      const handler = ({ detail }: VdsBasicSliderContinuousCustomEvent<number>) => {
        // Make sure that the value has been changed by the user, not by the value prop from the parent.
        if (valueRef.current !== detail) {
          onChangeCallback(detail);
        }
      };

      // Return a debounced handler if a delay greater than 0 was provided.
      return debounceDelay > 0 ? debounce(handler, debounceDelay) : handler;
    } else {
      return undefined;
    }
  }, [onChangeCallback, debounceDelay]);

  const disabled =
    isDisabled ||
    typeof minValue !== 'number' ||
    typeof maxValue !== 'number' ||
    typeof valueRef.current !== 'number';

  return (
    <VdsBasicSliderContinuous
      ref={elementRef}
      orientation="horizontal"
      size="large"
      minValue={minValue}
      maxValue={maxValue}
      /* The VDS component causes a runtime error if the value is undefined, despite matching the allowed types. */
      value={valueRef.current ?? 0} // Fallback to using a number to prevent the runtime error.
      step={step}
      /* There is no prop to configure the format/locale of the display label or display limits, so these features must be turned off. */
      displayLabel="off"
      displayLimits={false}
      disabled={disabled}
      /* onChangeInput is triggered by every change of the internal value; including initial setup, value change, and user interaction. */
      onChangeInput={onChangeInput}
    />
  );
};

export default Slider;
